import assertStatus from "@mittwald/api-client/dist/types/assertStatus";
import { usePathParams } from "@mittwald/flow-lib/dist/hooks/usePathParams";
import { refresh } from "@mittwald/react-use-promise";
import type { MittwaldApi } from "../../api/Mittwald";
import { mittwaldApi } from "../../api/Mittwald";
import sessionStore from "../../store/session";
import { Project } from "../project";
import type { AccessTokenRetrievalKey } from "../signup/Signup";
import { Signup } from "../signup/Signup";
import type Contributor from "./Contributor";
import type { MarketplaceContext } from "./Extension";
import Extension from "./Extension";

export type ExtensionInstanceApiData =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_Marketplace_ExtensionInstance;

export type AddToContextValues =
  MittwaldApi.Paths.V2_Extension_Instances.Post.Parameters.RequestBody;

export type ExtensionInstanceContract =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_Extension_ExtensionInstanceContract;

export type ExtensionInstanceTerminationData =
  MittwaldApi.Paths.V2_Extension_Instances_ExtensionInstanceId_Termination.Post.Responses.$201.Content.Application_Json;

export type CancelExtensionInstanceTerminationData =
  MittwaldApi.Paths.V2_Extension_Instances_ExtensionInstanceId_Termination.Delete.Responses.$200.Content.Application_Json;

export type OpenExtensionOrdersResponse =
  MittwaldApi.Paths.V2_Customers_CustomerId_Extension_Orders.Get.Responses.$200.Content.Application_Json;

interface ConsentToScopeValues {
  consentedScopes: string[];
}

class ExtensionInstance {
  public readonly data: ExtensionInstanceApiData;
  public readonly id: string;
  public readonly extensionId: string;
  public readonly isDisabled: boolean;
  public readonly contextId: string;
  public readonly consentedScopes: string[];
  public readonly hasProjectContext: boolean;
  public readonly hasCustomerContext: boolean;
  public readonly context: MarketplaceContext;

  public constructor(data: ExtensionInstanceApiData) {
    this.data = Object.freeze(data);
    this.id = data.id;
    this.contextId = data.aggregateReference.id;
    this.extensionId = data.extensionId;
    this.consentedScopes = data.consentedScopes;
    this.isDisabled = data.disabled;
    this.hasProjectContext = data.aggregateReference.domain === "project";
    this.hasCustomerContext = data.aggregateReference.domain === "customer";
    this.context = this.data.aggregateReference.aggregate as MarketplaceContext;
  }

  public static useGetCustomerId(extensionInstanceId: string): string {
    const extensionInstance =
      ExtensionInstance.useLoadById(extensionInstanceId);

    return extensionInstance.hasCustomerContext
      ? extensionInstance.contextId
      : Project.useLoadById(extensionInstance.contextId).customerId;
  }

  public static useLoadByPathParam(): ExtensionInstance {
    const { extensionInstanceId } = usePathParams("extensionInstanceId");
    return ExtensionInstance.useLoadById(extensionInstanceId);
  }

  public static async addToContext(
    values: AddToContextValues,
  ): Promise<string> {
    const response = await mittwaldApi.extensionCreateExtensionInstance.request(
      {
        requestBody: { ...values },
      },
    );

    assertStatus(response, 201);

    refresh({
      tag: "extension-instance-list",
    });

    return response.content.id;
  }

  public static async addToCustomerContext(
    values: Omit<AddToContextValues, "context">,
  ): Promise<string> {
    return await ExtensionInstance.addToContext({
      ...values,
      context: "customer",
    });
  }

  public static async addToProjectContext(
    values: Omit<AddToContextValues, "context">,
  ): Promise<string> {
    return await ExtensionInstance.addToContext({
      ...values,
      context: "project",
    });
  }

  public static useContract(
    extensionInstanceId: string,
  ): ExtensionInstanceContract | undefined {
    const response = mittwaldApi.extensionGetExtensionInstanceContract
      .getResource({
        path: {
          extensionInstanceId,
        },
      })
      .useWatchData({ optional: true });

    return response;
  }

  public static useLoadById(extensionInstanceId: string): ExtensionInstance {
    const data = mittwaldApi.extensionGetExtensionInstance
      .getResource({ path: { extensionInstanceId } })
      .useWatchData();

    return new ExtensionInstance(data);
  }

  public static useTryLoadById(
    extensionInstanceId: string | undefined,
  ): ExtensionInstance | undefined {
    const data = mittwaldApi.extensionGetExtensionInstance
      .getResource(
        extensionInstanceId ? { path: { extensionInstanceId } } : null,
      )
      .useWatchData({ optional: true });

    return data ? new ExtensionInstance(data) : undefined;
  }

  public static async tryLoadById(
    extensionInstanceId: string,
  ): Promise<ExtensionInstance | undefined> {
    const data = await mittwaldApi.extensionGetExtensionInstance.request({
      path: {
        extensionInstanceId,
      },
    });

    if (data.status !== 200) {
      return undefined;
    }

    return new ExtensionInstance(data.content);
  }

  public static async terminate(
    extensionInstanceId: string,
    instantTermination: boolean,
  ): Promise<ExtensionInstanceTerminationData> {
    const response =
      await mittwaldApi.extensionScheduleExtensionTermination.request({
        path: {
          extensionInstanceId,
        },
        requestBody: {
          instantTermination,
        },
      });

    assertStatus(response, 201);
    return response.content;
  }

  public static findAllOpenProjectExtensionOrders(
    projectId: string,
  ): OpenExtensionOrdersResponse {
    const data = mittwaldApi.extensionGetProjectExtensionInstanceOrders
      .getResource({
        path: { projectId },
      })
      .useWatchData({ optional: true });

    return data ?? [];
  }

  public static findAllOpenCustomerExtensionOrders(
    customerId: string,
  ): OpenExtensionOrdersResponse {
    const data = mittwaldApi.extensionGetCustomerExtensionInstanceOrders
      .getResource({ path: { customerId } })
      .useWatchData({ optional: true });

    return data ?? [];
  }

  public static async cancelTermination(
    extensionInstanceId: string,
  ): Promise<CancelExtensionInstanceTerminationData> {
    const response =
      await mittwaldApi.extensionCancelExtensionTermination.request({
        path: {
          extensionInstanceId,
        },
      });

    assertStatus(response, 200);
    return response.content;
  }

  public async consentToScopes(values: ConsentToScopeValues): Promise<void> {
    const response =
      await mittwaldApi.extensionConsentToExtensionScopes.request({
        path: { extensionInstanceId: this.id },
        requestBody: { ...values },
      });

    assertStatus(response, 204);
  }

  public async enable(): Promise<void> {
    const response = await mittwaldApi.extensionEnableExtensionInstance.request(
      { path: { extensionInstanceId: this.id } },
    );

    assertStatus(response, 204);
  }

  public async disable(): Promise<void> {
    const response =
      await mittwaldApi.extensionDisableExtensionInstance.request({
        path: { extensionInstanceId: this.id },
      });

    assertStatus(response, 204);
  }

  public async deleteForProject(): Promise<void> {
    const response = await mittwaldApi.extensionDeleteExtensionInstance.request(
      {
        path: { extensionInstanceId: this.id },
      },
    );

    refresh({
      tag: "extension-instance-list",
    });

    assertStatus(response, 204);
  }

  public async terminateExtensionInstanceForCustomer(): Promise<void> {
    const response =
      await mittwaldApi.extensionScheduleExtensionTermination.request({
        path: { extensionInstanceId: this.id },
      });

    refresh({
      tag: "extension-instance-list",
    });

    assertStatus(response, 201);
  }

  public async cancelExtensionInstanceTermination(
    extensionInstanceId: string,
  ): Promise<CancelExtensionInstanceTerminationData> {
    const response =
      await mittwaldApi.extensionCancelExtensionTermination.request({
        path: {
          extensionInstanceId,
        },
      });

    refresh({
      tag: "extension-instance-list",
    });

    assertStatus(response, 200);
    return response.content;
  }

  public async deleteForCustomer(): Promise<void> {
    const response = await mittwaldApi.extensionDeleteExtensionInstance.request(
      {
        path: { extensionInstanceId: this.id },
      },
    );

    refresh({
      tag: "extension-instance-list",
    });

    assertStatus(response, 204);
  }

  public useExtension(): Extension {
    return Extension.useLoadById(this.extensionId);
  }

  public useTryExtension(): Extension | undefined {
    return Extension.useTryLoadById(this.extensionId);
  }

  public async getExtension(): Promise<Extension> {
    return await Extension.loadById(this.extensionId);
  }

  public useContributor(): Contributor {
    return this.useExtension().useContributor();
  }

  public async getContributor(): Promise<Contributor> {
    return (await this.getExtension()).getContributor();
  }

  public static async getExtensionUrl(
    url: string,
    extensionInstanceId: string,
  ): Promise<string> {
    const accessTokenRetrievalKeyData =
      await Signup.getAccessTokenRetrievalKey(extensionInstanceId);

    return url
      .replace(":userId", accessTokenRetrievalKeyData.userId)
      .replace(
        ":accessTokenRetrievalKey",
        accessTokenRetrievalKeyData.accessTokenRetrievalKey,
      );
  }

  public async getSessionKey(): Promise<string> {
    const response = await mittwaldApi.extensionGenerateSessionToken.request({
      path: {
        extensionInstanceId: this.id,
        sessionId: sessionStore._sessionId,
      },
    });

    assertStatus(response, 200);
    return response.content.sessionToken;
  }

  public async getExtensionDetailsAction(
    accessTokenRetrievalKey: AccessTokenRetrievalKey,
    url: string | undefined,
  ): Promise<string | undefined> {
    const extension = await this.getExtension();

    if (url === undefined) {
      return undefined;
    }

    return url
      .replace(":userId", accessTokenRetrievalKey.userId)
      .replace(":contextId", this.data.aggregateReference.id)
      .replace(":context", this.context)
      .replace(":contributorId", extension.data.contributorId)
      .replace(":extensionId", this.data.extensionId)
      .replace(":extensionInstanceId", this.id)
      .replace(
        ":accessTokenRetrievalKey",
        accessTokenRetrievalKey.accessTokenRetrievalKey,
      )
      .replace(":apiVersion", "v1");
  }
}

export default ExtensionInstance;
